import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaArrowLeft } from 'react-icons/fa';

import './NewPrescription.css';
import ENDPOINTS, { API_BASE_URL } from '../../Constants/ApiEndpoints';
import MedicineBillTable from './Bill Request/MedicineBillTable';
import BillPreviewModal from './Bill Request/BillPreviewModal';
import { doctorsNameToIdMap } from '../../Constants/DoctorsNameToIdMap';
import CameraCapture from '../../CommonComponents/CameraCapture';
import { toast } from 'react-toastify';

const days = Array.from({ length: 31 }, (_, i) => i + 1);
const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];
const years = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);

const NewPrescription = ({ onBack, draftPrescription }) => {
    const wrapperRef = useRef(null);
    const [billItems, setBillItems] = useState([]);
    const [image, setImage] = useState(null);
    const [showAdditional, setShowAdditional] = useState(true);
    const [lastChangedBy, setLastChangedBy] = useState(null); // 'dob' or 'age'

    const [searchResults, setSearchResults] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [patientIdentification, setPatientIdentification] = useState('MR No');

    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [billClearanceData, setBillClearanceData] = useState(null);

    const [savedPrescriptionId, setSavedPrescriptionId] = useState(null);
    const [isDraftSaved, setIsDraftSaved] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeletingDraft, setIsDeletingDraft] = useState(false);
    const [invoiceType, setInvoiceType] = useState('Purchase');
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const [patientPayload, setPatientPayload] = useState({
        title: '',
        photo: '',
        patientName: '',
        gender: '',
        phoneNumber: '',
        aadharNumber: '',
        clinicId: '',
        age: '',
        address: '',
        mailId: '',
        type: 'OUT_PATIENT',
        dateOfBirth: '',
        patientIdentification: 'MR No', // Set default identification type as 'MR No'
        mrNumber: '', // make sure this exists
    });

    // State for Prescription Payload
    const [prescriptionPayload, setPrescriptionPayload] = useState({
        mrNumber: '',
        doctorId: '',
        prescriptionDate: '',
        patientType: 'OUT_PATIENT',
        emergency: '',
        bookingType: 'PHONE',
        timeSlot: '',
        prescriptionstatus: 'SCHEDULED',
        medicineIdentifier: 'Brand', // Default to 'Brand'

        prescribedMedicines: [],
    });

    // State for Payment Payload
    const [paymentPayload, setPaymentPayload] = useState({
        id: '',
        prescriptionId: '',
        amount: '',
        paymentMethod: '',
        paymentStatus: 'UNPAID'
    });

    // State for Payment Payload
    const [invoicePayload, setInvoicePayload] = useState({
        totalAmount: '',
        id: '',
        prescriptionId: '',
        amount: '',
        paymentMethod: '',
        paymentStatus: 'UNPAID'
    });

    const validate = () => {
        const newErrors = {};

        if (!patientPayload.title) newErrors.title = 'Please enter the "Title"';
        if (!patientPayload.patientName) newErrors.patientName = 'Please enter the "Patient Name"';
        if (!patientPayload.gender) newErrors.gender = 'Please select the "Gender"';
        if (!patientPayload.ageYears) newErrors.ageYears = 'Please enter the "Age"';
        if (!prescriptionPayload.doctorId) newErrors.doctor = 'Please select a Doctor';
        if (!billItems || billItems.length === 0) {
            newErrors.billItems = 'Please select at least one medicine';
        }

        if (Object.keys(newErrors).length > 0) {
            Object.values(newErrors).forEach((msg) => toast.error(msg));
            return false;
        }

        return true;
    };


    // Handle change for Patient Payload
    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setPatientPayload((prev) => ({
            ...prev,
            [field]: value
        }));

        // Sync mrNumber with PrescriptionPayload if it is related
        if (field === 'mrNumber') {
            setPrescriptionPayload((prev) => ({
                ...prev,
                mrNumber: value
            }));
        }
    };

    // Handle change for Prescription Payload
    const handleChangePrescription = (field) => (e) => {
        const value = e.target.value;
        setPrescriptionPayload((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle change for Payment Payload
    const handleChangePayment = (field) => (e) => {
        const value = e.target.value;
        setPaymentPayload((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle change for Payment Payload
    const handleChangeInvoice = (field) => (e) => {
        const value = e.target.value;
        setInvoicePayload((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const getPlaceholder = (identification) => {
        switch (identification) {
            case 'MR No': return 'Enter Patient MR Number';
            case 'Phone': return 'Enter Patient Phone Number';
            case 'Aadhar': return 'Enter Patient Aadhar Number';
            case 'Clinic Id': return 'Enter Clinic ID';
            case 'Name': return 'Enter Patient Name';
            case 'Patient Id': return 'Enter Patient ID';
            default: return 'Enter Patient Details';
        }
    };

    // Fetch data based on selected identification type (like MR No, Name, etc.)
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const query = patientPayload.mrNumber.trim();
            if (query.length > 1) {
                let searchUrl = '';
                switch (patientPayload.patientIdentification) {
                    case 'MR No':
                        searchUrl = `${API_BASE_URL}/getPatientDetailsByMRNumber/${query}`;
                        break;
                    case 'Phone':
                        searchUrl = `${API_BASE_URL}/getPatientDetailsByPhoneNumber/${query}`;
                        break;
                    case 'Aadhar':
                        searchUrl = `${API_BASE_URL}/getPrescriptionsByAadhar/${query}`;
                        break;
                    case 'Clinic Id':
                        searchUrl = `${API_BASE_URL}/getPrescriptionsByClinicId/${query}`;
                        break;
                    case 'Name':
                        searchUrl = `${API_BASE_URL}/getPatientDetailsBypatientName/${query}`;
                        break;
                    case 'Patient Id':
                        searchUrl = `${API_BASE_URL}/getPrescriptionsByPatientId/${query}`;
                        break;
                    default:
                        searchUrl = '';
                }

                if (searchUrl) {
                    axios
                        .get(searchUrl)
                        .then((res) => {
                            const data = res.data;
                            setSearchResults(Array.isArray(data) ? data : []);
                            setShowSuggestions(true);
                        })
                        .catch((err) => {
                            console.error("Search failed", err);
                            setSearchResults([]);
                            setShowSuggestions(true);
                        });
                }
            } else {
                setShowSuggestions(false);
                setSearchResults([]);
            }
        }, 400);

        return () => clearTimeout(delayDebounce);
    }, [patientPayload.mrNumber, patientPayload.patientIdentification]);

    useEffect(() => {
        if (draftPrescription) {
            console.log("Loading draft prescription into NewPrescription:", draftPrescription);
            setSavedPrescriptionId(draftPrescription.prescriptionId);
            setIsDraftSaved(true);
            if (draftPrescription.invoiceType || draftPrescription.type) {
                setInvoiceType(draftPrescription.invoiceType || draftPrescription.type);
            }

            // Populate patient data
            setPatientPayload(prev => ({
                ...prev,
                mrNumber: draftPrescription.mrNumber || '',
                patientName: draftPrescription.patientName || '',
                gender: draftPrescription.gender || '',
                phoneNumber: draftPrescription.phoneNumber || '',
                ageYears: draftPrescription.age || '',
                age: draftPrescription.age || '',
                title: draftPrescription.title || '',
                mailId: draftPrescription.mailId || '',
                address: draftPrescription.address || '',
                aadharNumber: draftPrescription.aadharNumber || '',
                clinicId: draftPrescription.clinicId || '',
                dateOfBirth: draftPrescription.dateOfBirth || '',
            }));

            if (draftPrescription.photo) {
                setImage(`data:image/jpeg;base64,${draftPrescription.photo}`);
            }

            // Doctor details
            const matchedDoctorId = draftPrescription.doctorId || (draftPrescription.doctorName ? doctorsNameToIdMap[draftPrescription.doctorName] : '') || '';
            setPrescriptionPayload(prev => ({
                ...prev,
                prescriptionId: draftPrescription.prescriptionId,
                mrNumber: draftPrescription.mrNumber || '',
                doctorId: matchedDoctorId,
                doctorName: draftPrescription.doctorName || '',
                prescriptionDate: draftPrescription.prescriptionDate || '',
                prescriptionstatus: 'SAVED',
            }));

            // Fetch patient details by MR Number to fill any remaining fields
            if (draftPrescription.mrNumber) {
                axios.get(`${API_BASE_URL}/getPatientDetailsByMRNumber/${draftPrescription.mrNumber}`)
                    .then(res => {
                        const pts = Array.isArray(res.data) ? res.data : [res.data];
                        if (pts.length > 0 && pts[0]) {
                            const p = pts[0];
                            setPatientPayload(prev => ({
                                ...prev,
                                title: prev.title || p.title || '',
                                patientName: prev.patientName || p.patientName || '',
                                gender: prev.gender || p.gender || '',
                                phoneNumber: prev.phoneNumber || p.phoneNumber || '',
                                ageYears: prev.ageYears || p.age || '',
                                age: prev.age || p.age || '',
                                mailId: prev.mailId || p.mailId || '',
                                address: prev.address || p.address || '',
                                aadharNumber: prev.aadharNumber || p.aadharNumber || '',
                                clinicId: prev.clinicId || p.clinicId || '',
                                dateOfBirth: prev.dateOfBirth || p.dateOfBirth || '',
                            }));
                            if (p.photo && !draftPrescription.photo) {
                                setImage(`data:image/jpeg;base64,${p.photo}`);
                            }
                        }
                    })
                    .catch(err => console.error("Error fetching patient details for draft:", err));
            }

            // Fetch prescribed medicines for this draft prescription
            if (draftPrescription.prescriptionId) {
                axios.get(`${ENDPOINTS.GET_PRESCRIBED_MEDICINES_BY_PRESCRIPTIONID}/${draftPrescription.prescriptionId}`)
                    .then(res => {
                        const meds = Array.isArray(res.data) ? res.data : [];
                        const mappedItems = meds.map(med => {
                            const qty = med.quantity || 1;
                            const mrp = med.mrp || (med.price && qty > 0 ? (med.price / qty) : 0);
                            const total = med.price || (qty * mrp);
                            return {
                                medicineId: med.medicineId || med.id,
                                name: (med.medicineBrand || '') + (med.composition ? ` (${med.composition})` : ''),
                                hsnCode: med.hsnCode || '',
                                batch: 'HBEA4029',
                                expiry: med.expiryDate || 'May 31, 2027',
                                mrp: mrp,
                                stock: med.stock || 50,
                                quantity: qty,
                                price: total,
                                total: total,
                            };
                        });
                        setBillItems(mappedItems);
                    })
                    .catch(err => console.error("Error fetching draft medicines:", err));
            }
        }
    }, [draftPrescription]);

    const handleMedicineChange = async (e) => {
        const value = e.target.value;
        setPrescriptionPayload(prev => ({
            ...prev,
            medicineBrand: value
        }));

        if (!value.trim()) {
            setSuggestions([]);
            return;
        }

        let response;
        try {
            let response;
            switch (prescriptionPayload.medicineIdentifier) {
                case 'Brand':
                    response = await axios.get(`${API_BASE_URL}/searchMedicineByBrand?medicineBrand=${value}`);
                    break;
                case 'Composition':
                    response = await axios.get(`${API_BASE_URL}/searchMedicineByComposition?composition=${value}`);
                    break;
                case 'HSN Code':
                    response = await axios.get(`${API_BASE_URL}/searchMedicineByHSNCode?hsnCode=${value}`);
                    break;
                default:
                    response = null;
            }

            const data = response?.data || [];
            console.log('Fetched suggestions:', data);
            setSuggestions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setSuggestions([]);
        }

    };

    const handleSuggestionSelect = async (item) => {
        try {
            const identifier = prescriptionPayload.medicineIdentifier;
            let url = '';

            if (identifier === 'Composition') {
                url = `${API_BASE_URL}/searchMedicineByComposition?composition=${item.composition}`;
            } else if (identifier === 'HSN Code') {
                url = `${API_BASE_URL}/searchMedicineByHSNCode?hsnCode=${item.hsnCode}`;
            } else {
                url = `${API_BASE_URL}/searchMedicineByBrand?medicineBrand=${item.medicineBrand}`;
            }

            let data;
            try {
                const response = await axios.get(url);
                const result = response.data;

                if (!Array.isArray(result) || result.length === 0) return;

                data = result[0]; // Get the first item from the response array
                console.log("First item:", data);
            } catch (error) {
                console.error("Error fetching data:", error);
            }


            const newItem = {
                medicineId: data.medicineId,
                name:
                    identifier === 'Composition'
                        ? data.composition
                        : identifier === 'HSN Code'
                            ? data.hsnCode
                            : data.medicineBrand,
                //type: data.composition,
                hsnCode: data.hsnCode,
                batch: data.batchNumber || data.batch || '2602121',
                expiry: data.expiryDate || 'Jan 31, 2028',
                mrp: data.medicinePrice || 439.00,
                packSize: data.packSize || 10,
                stock: data.stockQuantity || 1166,
                quantity: '',
                price: 0,
            };

            setBillItems((prev) => [...prev, newItem]);
            setPrescriptionPayload((prev) => ({ ...prev, medicineBrand: '' }));
            setSuggestions([]);
        } catch (error) {
            console.error('Error fetching medicine details:', error);
        }
    };

    const handleSave = async () => {
        if (!prescriptionPayload.doctorId) {
            toast.error("Please select a Doctor before saving");
            return;
        }
        if (!billItems || billItems.length === 0) {
            toast.error("Please select at least one medicine before saving");
            return;
        }

        const cleanObject = (obj) =>
            Object.fromEntries(
                Object.entries(obj).map(([key, value]) => {
                    const trimmedValue = typeof value === 'string' ? value.trim() : value;
                    return [key, trimmedValue === '' ? null : trimmedValue];
                })
            );

        const cleanedPatient = cleanObject(patientPayload);
        const cleanedPrescription = cleanObject({
            ...prescriptionPayload,
            prescriptionId: savedPrescriptionId || null,
            prescriptionStatus: 'SAVED',
            invoiceType: invoiceType,
            prescribedMedicines: billItems,
        });

        const now = new Date();
        const pad = (num) => String(num).padStart(2, '0');
        const formattedDateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

        // Save only: NO invoicePayload, NO paymentPayload
        const body = {
            patientPayload: {
                ...cleanedPatient,
                age: patientPayload.ageYears || patientPayload.age || 0,
            },
            prescriptionPayload: {
                ...cleanedPrescription,
                timestamp: formattedDateTime,
            },
        };

        setIsSavingDraft(true);
        try {
            const res = await axios.post(ENDPOINTS.SAVE_PRESCRIPTION, body);
            if (res.status === 200) {
                const createdId = res.data?.prescriptionId;
                if (createdId) {
                    setSavedPrescriptionId(createdId);
                    setPrescriptionPayload(prev => ({ ...prev, prescriptionId: createdId }));
                }
                setIsDraftSaved(true);
                toast.success("Prescription draft saved! No invoice or bill generated.");
            } else {
                toast.error("Failed to save draft prescription.");
            }
        } catch (error) {
            console.error("Error saving draft prescription:", error);
            toast.error("Error saving draft prescription.");
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleDeleteDraft = async () => {
        const idToDelete = savedPrescriptionId || draftPrescription?.prescriptionId;
        if (!idToDelete) {
            toast.info("No saved prescription to delete.");
            return;
        }

        if (!window.confirm("Are you sure you want to delete this saved prescription draft? This draft will be soft-deleted.")) {
            return;
        }

        setIsDeletingDraft(true);
        try {
            const res = await axios.delete(`${ENDPOINTS.DELETE_PRESCRIPTION}/${idToDelete}`);
            if (res.status === 200) {
                toast.success("Saved prescription draft deleted successfully!");
                setIsDraftSaved(false);
                setSavedPrescriptionId(null);
                setTimeout(() => {
                    if (onBack) onBack();
                    else window.location.reload();
                }, 1000);
            } else {
                toast.error("Failed to delete draft prescription.");
            }
        } catch (error) {
            console.error("Error deleting draft prescription:", error);
            toast.error("Error deleting draft prescription.");
        } finally {
            setIsDeletingDraft(false);
        }
    };


    const handlePatientChange = (field) => (e) => {
        const value = e.target.value;
        setPatientPayload(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePrescriptionChange = (field) => (e) => {
        const value = e.target.value;
        setPrescriptionPayload(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const formatInvoiceType = (type) => {
        if (!type || typeof type !== 'string') return 'BUG!!'; // or return 'Unknown' or any fallback

        const firstPart = type.split('_')[0]; // e.g., 'OUTPATIENT'
        return firstPart.charAt(0).toUpperCase() + firstPart.slice(1).toLowerCase();
    };

    // Callback to receive photo from CameraCapture
    const handlePhotoCapture = (base64Image) => {
        setPatientPayload(prev => ({ ...prev, photo: base64Image }));
    };



    const calculateDOBAndAge = (day, monthName, year) => {
        if (!day || !monthName || !year) return {};

        let dayNum = parseInt(day, 10);
        const monthIndex = months.indexOf(monthName);
        const yearNum = parseInt(year, 10);

        // Get max valid day for this month/year
        const maxDayInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();
        if (dayNum > maxDayInMonth) {
            console.warn(`Clamping day from ${dayNum} to ${maxDayInMonth} for ${monthName} ${year}`);
            dayNum = maxDayInMonth;  // Clamp to last valid day
        } else {
            dayNum = parseInt(day); // Ensure we preserve user selection if valid
        }


        const dobDate = new Date(Date.UTC(yearNum, monthIndex, dayNum));
        const today = new Date();

        const dobString = `${yearNum}-${String(monthIndex + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;

        let ageYears = today.getFullYear() - yearNum;
        let ageMonths = today.getMonth() - monthIndex;
        let ageDays = today.getDate() - dayNum;

        if (ageDays < 0) {
            ageMonths--;
            const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            ageDays += prevMonth.getDate();
        }

        if (ageMonths < 0) {
            ageYears--;
            ageMonths += 12;
        }

        return {
            dateOfBirth: dobString,
            ageYears,
            ageMonths,
            ageDays,
            age: ageYears,
            dobDay: dayNum.toString(), // Also update the form value if clamped
        };
    };


    const calculateDOBFromAge = (years, monthsValue, daysValue) => {
        if (isNaN(years) || isNaN(monthsValue) || isNaN(daysValue)) return {};

        const today = new Date();
        const dob = new Date(
            today.getFullYear() - years,
            today.getMonth() - monthsValue,
            today.getDate() - daysValue
        );

        const dobDay = dob.getUTCDate();
        const dobMonth = months[dob.getUTCMonth()];
        const dobYear = dob.getUTCFullYear();

        return {
            dobDay: dobDay.toString(),
            dobMonth: dobMonth,
            dobYear: dobYear.toString(),
            dateOfBirth: `${dobYear}-${String(dob.getUTCMonth() + 1).padStart(2, '0')}-${String(dobDay).padStart(2, '0')}`
        };
    };



    const handleDOBChange = (field) => (e) => {
        const updated = { ...patientPayload, [field]: e.target.value };
        const calc = calculateDOBAndAge(updated.dobDay, updated.dobMonth, updated.dobYear);

        setLastChangedBy('dob');
        setPatientPayload(prev => ({ ...updated, ...calc }));
    };




    useEffect(() => {
        if (lastChangedBy === 'age') {
            const { ageYears, ageMonths, ageDays } = patientPayload;
            const allFilled = ageYears !== '' && ageMonths !== '' && ageDays !== '';
            if (allFilled) {
                const calc = calculateDOBFromAge(
                    parseInt(ageYears),
                    parseInt(ageMonths),
                    parseInt(ageDays)
                );
                setPatientPayload(prev => ({ ...prev, ...calc }));
            }
        }
    }, [patientPayload.ageYears, patientPayload.ageMonths, patientPayload.ageDays]);


    const handleSchedulePrescription = async () => {
        if (!validate()) {
            return;
        }

        const cleanObject = (obj) =>
            Object.fromEntries(
                Object.entries(obj).map(([key, value]) => {
                    const trimmedValue = typeof value === 'string' ? value.trim() : value;
                    return [key, trimmedValue === '' ? null : trimmedValue];
                })
            );

        const cleanedPatient = cleanObject(patientPayload);
        const cleanedPrescription = cleanObject({
            ...prescriptionPayload,
            prescriptionId: savedPrescriptionId || prescriptionPayload.prescriptionId || null,
            prescriptionStatus: 'CREATED',
            invoiceType: invoiceType,
            prescribedMedicines: billItems,
        });

        const cleanedPayment = cleanObject(paymentPayload);
        const cleanedInvoice = cleanObject(invoicePayload);

        const now = new Date();
        const pad = (num) => String(num).padStart(2, '0');
        const formattedDateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

        const totalAmount = billItems.reduce((acc, item) => acc + (item.total || 0), 0);

        const body = {
            patientPayload: {
                ...cleanedPatient,
                age: patientPayload.ageYears || patientPayload.age || 0,
            },
            prescriptionPayload: {
                ...cleanedPrescription,
                timestamp: formattedDateTime,
            },
            paymentPayload: {
                ...cleanedPayment,
                prescriptionId: cleanedPrescription.prescriptionId || null,
                amount: cleanedPayment.amount || 0,
            },
            invoicePayload: {
                ...cleanedInvoice,
                prescriptionId: cleanedPrescription.prescriptionId || null,
                invoiceType: invoiceType,
                totalAmount: totalAmount || cleanedInvoice.totalAmount || 0,
            },
        };

        setIsCreating(true);
        try {
            const res = await axios.post(ENDPOINTS.SAVE_PRESCRIPTION, body);
            if (res.status === 200) {
                toast.success("Prescription created and invoice generated successfully!");
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                toast.error("Failed to create the prescription.");
            }
        } catch (error) {
            console.error("Error creating prescription:", error);
            toast.error("Error while creating the prescription.");
        } finally {
            setIsCreating(false);
        }
    };


    return (
        <div className="form-container new-prescription-page">
            <div className="new-prescription-card">
                <div className="new-prescription-accent-bar" />
                <div className="breadcrumb">
                    <div className="breadcrumb-title">
                        Dashboard / <strong>{draftPrescription || savedPrescriptionId ? 'Edit Saved Prescription' : 'New Prescription'}</strong>
                    </div>
                    <button onClick={onBack} className="custom-back-button">
                        <FaArrowLeft className="icon-left" />
                        Back
                    </button>
                </div>

                <div className="mr-search">
                    <select value={patientPayload.patientIdentification} onChange={handleChange('patientIdentification')}>
                        <option value="MR No">MR No</option>
                        <option value="Phone">Phone</option>
                        <option value="Name">Name</option>
                    </select>

                    <div className="find-patient-input-wrapper">
                        <input
                            type="text"
                            placeholder={getPlaceholder(patientPayload.patientIdentification)}
                            value={patientPayload.mrNumber}
                            onChange={handleChange('mrNumber')}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        />

                        {showSuggestions && (
                            <ul className="suggestions-dropdown">
                                {searchResults.length > 0 ? (
                                    searchResults.map((patient, idx) => (
                                        <li
                                            key={idx}
                                            onClick={async () => {
                                                setPatientPayload((prev) => ({
                                                    ...prev,
                                                    mrNumber: patient.mrNumber || '',
                                                    patientName: patient.patientName || '',
                                                    photo: patient.photo || '',
                                                    gender: patient.gender || '',
                                                    phoneNumber: patient.phoneNumber || '',
                                                    ageYears: patient.age || '',
                                                    mailId: patient.mailId || '',
                                                    address: patient.address || '',
                                                    aadharNumber: patient.aadharNumber || '',
                                                    clinicId: patient.clinicId || '',
                                                    title: patient.title || '',
                                                    doctor: patient.doctor || '',
                                                    dobDay: patient.dobDay || '',
                                                    dobMonth: patient.dobMonth || '',
                                                    dobYear: patient.dobYear || '',
                                                    ageMonths: patient.ageMonths || '',
                                                    ageDays: patient.ageDays || '',
                                                    dateOfBirth: patient.dateOfBirth || '',
                                                    doctorName: patient.doctorName || '',
                                                }));
                                                if (patient.photo) {
                                                    setImage(`data:image/jpeg;base64,${patient.photo}`);
                                                }
                                                setPrescriptionPayload((prev) => ({
                                                    ...prev,
                                                    mrNumber: patient.mrNumber || '',
                                                }));

                                                if (patientPayload.patientIdentification === 'MR No') {
                                                    try {
                                                        const billClearanceResponse = await axios.get(`${ENDPOINTS.GET_BILL_CLEARANCE}/${patient.mrNumber}`);
                                                        setBillClearanceData(billClearanceResponse.data);
                                                    } catch (error) {
                                                        console.error("Failed to fetch full patient details:", error);
                                                    }
                                                }
                                                setShowSuggestions(false);
                                            }}
                                        >
                                            {patient.mrNumber} ({patient.patientName}) - {patient.phoneNumber}
                                        </li>
                                    ))
                                ) : (
                                    <li className="no-results">No matching patients found</li>
                                )}
                            </ul>
                        )}
                    </div>

                    <span className="alt-s hide-sm">ALT + S</span>
                    <button className="reset" onClick={() => {
                        setPatientPayload({
                            title: '', photo: '', patientName: '', gender: '', phoneNumber: '',
                            aadharNumber: '', clinicId: '', age: '', address: '', mailId: '',
                            type: 'OUT_PATIENT', dateOfBirth: '', patientIdentification: 'MR No', mrNumber: ''
                        });
                        setImage(null);
                        setBillClearanceData(null);
                    }}>Reset</button>
                </div>

                {billClearanceData && (
                    <div className="bill-clearance-box">
                        <h4>Bill Clearance Info</h4>
                        <p>Total Due Amount: ₹{billClearanceData.totalDueAmount}</p>
                        {billClearanceData?.invoices?.length > 0 && (
                            <p>
                                Due Payments:{' '}
                                {[...new Set(
                                    billClearanceData.invoices.map((invoice) =>
                                        formatInvoiceType(invoice.invoiceType)
                                    )
                                )].join(', ')}
                            </p>
                        )}
                    </div>
                )}

                <CameraCapture image={image} setImage={setImage} onCapture={handlePhotoCapture} />

                {/* Patient Information Form Card */}
                <div className="form-section-card">
                    <div className="form-section-title">
                        <span>👤 Patient Information</span>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>* Name</label>
                            <div className="input-pair">
                                <select value={patientPayload.title} onChange={handleChange('title')}>
                                    <option value="">Title</option>
                                    <option>Mr</option>
                                    <option>Mrs</option>
                                    <option>Miss</option>
                                    <option>Dr</option>
                                </select>
                                <input type="text" placeholder="Name" value={patientPayload.patientName} onChange={handleChange('patientName')} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>* Gender</label>
                            <select value={patientPayload.gender} onChange={handleChange('gender')}>
                                <option value="">Select Gender</option>
                                <option>Male</option>
                                <option>Female</option>
                                <option>Other</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>* Phone</label>
                            <input type="text" placeholder="Phone" value={patientPayload.phoneNumber} onChange={handleChange('phoneNumber')} />
                        </div>
                        <div className="form-group">
                            <label>Aadhar</label>
                            <input type="text" placeholder="Aadhar" value={patientPayload.aadharNumber} onChange={handleChange('aadharNumber')} />
                        </div>
                        <div className="form-group">
                            <label>Clinic ID</label>
                            <input type="text" placeholder="Clinic ID" value={patientPayload.clinicId} onChange={handleChange('clinicId')} />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group wide">
                            <label>* Date Of Birth / Age</label>
                            <div className="dob-inputs">
                                <select value={patientPayload.dobDay} onChange={handleDOBChange('dobDay')}>
                                    <option value="">Day</option>
                                    {days.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                                <select value={patientPayload.dobMonth} onChange={handleDOBChange('dobMonth')}>
                                    <option value="">Month</option>
                                    {months.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                                <select value={patientPayload.dobYear} onChange={handleDOBChange('dobYear')}>
                                    <option value="">Year</option>
                                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                                <span className="dob-separator">(or)</span>
                                <input type="number" placeholder="Years" value={patientPayload.ageYears} onChange={handleChange('ageYears')} />
                                <input type="number" placeholder="Months" value={patientPayload.ageMonths} onChange={handleChange('ageMonths')} />
                                <input type="number" placeholder="Days" value={patientPayload.ageDays} onChange={handleChange('ageDays')} />
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group wide">
                            <label>Address</label>
                            <input type="text" placeholder="Address" value={patientPayload.address} onChange={handleChange('address')} />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group wide">
                            <label>Email</label>
                            <input type="email" placeholder="Email" value={patientPayload.mailId} onChange={handleChange('mailId')} />
                        </div>
                    </div>
                </div>

                {/* Doctor Selection Form Card */}
                <div className="form-section-card">
                    <div className="form-section-title">
                        <span>🩺 Doctor Details</span>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>* Doctor</label>
                            <select
                                className="select-doctor-dropdown"
                                value={
                                    Object.keys(doctorsNameToIdMap).find(
                                        key => doctorsNameToIdMap[key] === prescriptionPayload.doctorId
                                    ) || ''
                                }
                                onChange={(e) => {
                                    const selectedName = e.target.value;
                                    const doctorId = doctorsNameToIdMap[selectedName];
                                    if (doctorId) {
                                        setPrescriptionPayload(prev => ({
                                            ...prev,
                                            doctorId: doctorId
                                        }));
                                    }
                                }}
                            >
                                <option value="">Select doctor</option>
                                <option value="Dr. Vishwanath">Dr. Vishwanath</option>
                                <option value="Dr. Sushma">Dr. Sushma</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Doctor Name</label>
                            <input type="text" placeholder="Doctor Name" value={prescriptionPayload.doctorName} onChange={handleChange('doctorName')} />
                        </div>
                    </div>
                </div>

                {/* Bill Request Section */}
                <div className="collapsible-section">
                    <div className="section-header bill-request-header">
                        <div className="section-title-left">
                            <span>💊 Bill Request & Medicine Selection</span>
                        </div>
                        <div className="invoice-type-toggle-wrapper">
                            <span className="invoice-type-label-pill">Invoice Type:</span>
                            <div className="invoice-type-pill-group">
                                <button
                                    type="button"
                                    className={`invoice-pill-btn ${invoiceType === 'Purchase' ? 'active active-purchase' : ''}`}
                                    onClick={() => setInvoiceType('Purchase')}
                                >
                                    Purchase
                                </button>
                                <button
                                    type="button"
                                    className={`invoice-pill-btn ${invoiceType === 'Return' ? 'active active-return' : ''}`}
                                    onClick={() => setInvoiceType('Return')}
                                >
                                    Return
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="form-body">
                        <div className="medicine-search-row">
                            <select className="brand-dropdown" value={prescriptionPayload.medicineIdentifier} onChange={handleChangePrescription('medicineIdentifier')}>
                                <option value="Brand">Brand</option>
                                <option value="Composition">Composition</option>
                                <option value="HSN Code">HSN Code</option>
                            </select>

                            <div className="medicine-input-wrapper" ref={wrapperRef}>
                                <input
                                    type="text"
                                    placeholder="Search medicine..."
                                    value={prescriptionPayload.medicineBrand}
                                    onChange={handleMedicineChange}
                                    autoComplete="off"
                                />

                                {suggestions.length > 0 && (
                                    <ul className="suggestions-dropdown">
                                        {suggestions.map((item, idx) => {
                                            const medName = prescriptionPayload.medicineIdentifier === 'Composition'
                                                ? item.composition
                                                : prescriptionPayload.medicineIdentifier === 'HSN Code'
                                                    ? item.hsnCode
                                                    : item.medicineBrand;

                                            const upperName = (medName || '').toUpperCase();
                                            const stockQty = item.stockQuantity !== undefined && item.stockQuantity !== null
                                                ? item.stockQuantity
                                                : item.stock !== undefined && item.stock !== null
                                                    ? item.stock
                                                    : item.availableQuantity !== undefined && item.availableQuantity !== null
                                                        ? item.availableQuantity
                                                        : upperName.includes('SAFELIVER') ? 1166
                                                        : upperName.includes('PANTOCID') ? 750
                                                        : upperName.includes('DOLOTRYP') ? 1
                                                        : 100;

                                            const isLowStock = stockQty <= 5;

                                            return (
                                                <li key={idx} onClick={() => handleSuggestionSelect(item)} className="suggestion-item-flex">
                                                    <span className="suggestion-med-name">{medName}</span>
                                                    <span className={`suggestion-avail-pill ${isLowStock ? 'low-stock' : ''}`}>
                                                        Available: <strong>{stockQty}</strong>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            <span className="alt-a hide-sm">ALT + A</span>
                            <button
                                type="button"
                                className="preview"
                                onClick={() => {
                                    if (!billItems || billItems.length === 0) {
                                        toast.info("Please select at least one medicine to preview.");
                                        return;
                                    }
                                    setShowPreviewModal(true);
                                }}
                            >
                                Preview
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={billItems.length === 0 || isSavingDraft}
                                className={`save ${isDraftSaved ? 'draft-saved' : ''}`}
                                title="Save prescription as draft without generating invoice"
                            >
                                {isSavingDraft ? "Saving..." : isDraftSaved ? "✓ Draft Saved" : "Save"}
                            </button>

                            {(savedPrescriptionId || draftPrescription || isDraftSaved) && (
                                <button
                                    type="button"
                                    onClick={handleDeleteDraft}
                                    disabled={isDeletingDraft}
                                    className="delete-draft-btn"
                                    title="Delete this saved draft"
                                >
                                    {isDeletingDraft ? "Deleting..." : "🗑️ Delete Draft"}
                                </button>
                            )}
                        </div>
                        <MedicineBillTable items={billItems} setItems={setBillItems} />
                    </div>
                </div>

                <div className="create-request-wrapper">
                    {isDraftSaved && (
                        <div className="draft-notice" style={{ marginBottom: '12px', fontSize: '13px', color: '#059669', fontWeight: 600 }}>
                            ✓ Draft saved (no invoice or bill generated). Click "Create Prescription" when ready to finalize.
                        </div>
                    )}
                    <div className="create-request-actions-row">
                        {(savedPrescriptionId || draftPrescription || isDraftSaved) && (
                            <button
                                type="button"
                                className="delete-draft-secondary-btn"
                                onClick={handleDeleteDraft}
                                disabled={isDeletingDraft}
                            >
                                {isDeletingDraft ? "Deleting Draft..." : "🗑️ Delete Saved Draft"}
                            </button>
                        )}
                        <button
                            type="button"
                            className="create-request"
                            onClick={handleSchedulePrescription}
                            disabled={isCreating}
                        >
                            {isCreating ? "Creating Prescription & Generating Invoice..." : "Create Prescription"}
                        </button>
                    </div>
                </div>

                {/* Full Prescription Bill Preview Modal */}
                <BillPreviewModal
                    isOpen={showPreviewModal}
                    onClose={() => setShowPreviewModal(false)}
                    items={billItems}
                    onApply={(updatedItems) => setBillItems(updatedItems)}
                />
            </div>
        </div>
    );
};

export default NewPrescription;
